import { z } from 'zod';
import { dateTimeSchema, idSchema, jsonObjectSchema } from './common';

/** SPEC §10 — append-only audit log. */

export const AUDIT_ACTIONS = [
  'user.login',
  'user.invite',
  'user.disable',
  'batch.import',
  'batch.delete',
  'row.edit',
  'row.override',
  'asset.generate',
  'asset.regenerate',
  'asset.select_take',
  'row.approve',
  // SPEC-GAP: §10 lists `row.approve` but not its failure. Approval mutates GCS
  // outside the database transaction, so every attempt that actually touched an
  // object has to leave a trail even when the approval is rejected or rolled back.
  'row.publish_failed',
  'row.unapprove',
  'batch.export',
  'settings.update',
  'run.start',
  'run.abort',
  // SPEC-GAP: §9 defines enable / re-invite / logout / accept-invite endpoints but
  // §10 does not list their actions. Added for symmetry; nothing else depends on them.
  'user.logout',
  'user.enable',
  'user.reinvite',
  'user.accept_invite',
  'user.update',
  'batch.rename',
  'row.create',
  'row.remove',
] as const;
export const auditActionSchema = z.enum(AUDIT_ACTIONS);
export type AuditAction = z.infer<typeof auditActionSchema>;

export const AUDIT_ENTITY_TYPES = [
  'User',
  'Session',
  'Batch',
  'Row',
  'AudioAsset',
  'Take',
  'GenerationRun',
  'Settings',
] as const;
export const auditEntityTypeSchema = z.enum(AUDIT_ENTITY_TYPES);
export type AuditEntityType = z.infer<typeof auditEntityTypeSchema>;

export const auditLogSchema = z.object({
  id: idSchema,
  actorId: idSchema.nullable(),
  actorName: z.string().nullable(),
  actorEmail: z.string().nullable(),
  action: auditActionSchema,
  entityType: auditEntityTypeSchema,
  entityId: z.string(),
  metadata: jsonObjectSchema,
  createdAt: dateTimeSchema,
});
export type AuditLog = z.infer<typeof auditLogSchema>;

/** GET /api/audit — SUPER_ADMIN, filterable feed. */
export const listAuditQuerySchema = z.object({
  action: auditActionSchema.optional(),
  entityType: auditEntityTypeSchema.optional(),
  entityId: z.string().max(64).optional(),
  actorId: idSchema.optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type ListAuditQuery = z.infer<typeof listAuditQuerySchema>;

export const listAuditResponseSchema = z.object({
  items: z.array(auditLogSchema),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  hasMore: z.boolean(),
});
export type ListAuditResponse = z.infer<typeof listAuditResponseSchema>;

/** GET /api/rows/:id/history — the per-row history drawer. */
export const rowHistoryResponseSchema = z.object({
  items: z.array(auditLogSchema),
});
export type RowHistoryResponse = z.infer<typeof rowHistoryResponseSchema>;

/** Typed metadata payloads (documentation + helpers; stored as plain JSON). */
export const rowEditMetadataSchema = z.object({
  before: z.object({ englishText: z.string(), arabicText: z.string() }),
  after: z.object({ englishText: z.string(), arabicText: z.string() }),
  staleLanguages: z.array(z.string()),
});
export type RowEditMetadata = z.infer<typeof rowEditMetadataSchema>;

export const rowApproveMetadataSchema = z.object({
  publishedSlug: z.string(),
  paths: z.array(
    z.object({
      language: z.string(),
      publishedPath: z.string(),
      deliveredPath: z.string().nullable(),
      generationOld: z.string().nullable(),
      generationNew: z.string(),
      archivedPath: z.string().nullable(),
    }),
  ),
});
export type RowApproveMetadata = z.infer<typeof rowApproveMetadataSchema>;

export const settingsUpdateMetadataSchema = z.object({
  /** Changed key paths only — never values, never secrets (SPEC §10). */
  changedPaths: z.array(z.string()),
});
export type SettingsUpdateMetadata = z.infer<typeof settingsUpdateMetadataSchema>;

export const batchExportMetadataSchema = z.object({
  rowCount: z.number().int().min(0),
  approvedRowCount: z.number().int().min(0),
  staleRowCount: z.number().int().min(0),
  filename: z.string(),
});
export type BatchExportMetadata = z.infer<typeof batchExportMetadataSchema>;
